import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Music2, LogIn } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="mx-auto mb-4 inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/95 border border-zinc-800 shadow-md" data-testid="login-brand">
            <img src="/logo.png" alt="Sonotek Digital" className="h-10 w-auto" />
          </div>
          <div className="text-sm text-zinc-500 mt-2">Sonotek<span className="gradient-text font-semibold">Digi</span></div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-zinc-500">Sign in to manage your submissions</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 p-8 rounded-2xl border border-zinc-800 bg-zinc-950/60">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
            <Input id="email" data-testid="login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-zinc-900 border-zinc-800 focus-visible:ring-cyan-500/50" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
              <Link to="/forgot-password" className="text-xs text-cyan-400 hover:underline" data-testid="forgot-link">Forgot?</Link>
            </div>
            <Input id="password" data-testid="login-password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-zinc-900 border-zinc-800 focus-visible:ring-cyan-500/50" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110">
            <LogIn className="w-4 h-4 mr-2" /> {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-zinc-500">
          Accounts are provisioned by the SonotekDigi team. Contact your admin for access.
        </p>
      </div>
    </div>
  );
}
